import { http } from '@noor/api';
import {
  dashboardSummarySchema,
  type DashboardParams,
  type DashboardSummary,
  type DashboardKpis,
  type DashboardSales,
  type CourierTracking,
} from '../model/schema';
import { normalizeDashboardDto } from '../model/contract';
import { getSeedDashboard } from '../model/seed';

export const dashboardApi = {
  /**
   * Fetches dashboard analytics summary according to backend contract.
   * Dispatches GET request to `admin/dashboard` (intercepted by MSW mock service
   * in development mode, or answered by the backend API in staging/production).
   */
  async getSummary(params?: DashboardParams): Promise<DashboardSummary> {
    try {
      const query: Record<string, string> = {};
      if (params?.tab) query.tab = params.tab;
      if (params?.period) query.period = params.period;
      if (params?.date) query.date = params.date;

      const raw = await http.get<unknown>('admin/dashboard', { query });
      const normalized = normalizeDashboardDto(raw);
      return dashboardSummarySchema.parse(normalized);
    } catch {
      return getSeedDashboard(params?.tab, params?.period, params?.date);
    }
  },

  /**
   * Fetches KPI stat cards from `/admin/dashboard/kpis`.
   */
  async getKpis(params?: DashboardParams): Promise<DashboardKpis> {
    const summary = await this.getSummary(params);
    return summary.kpis;
  },

  /**
   * Fetches sales dynamics series from `/admin/dashboard/sales`.
   */
  async getSales(params?: DashboardParams): Promise<DashboardSales> {
    const summary = await this.getSummary(params);
    return summary.sales;
  },

  /**
   * Fetches courier tracking status from `/admin/dashboard/couriers-status`.
   */
  async getCourierTracking(params?: DashboardParams): Promise<CourierTracking> {
    const summary = await this.getSummary(params);
    return summary.courierTracking;
  },

  /**
   * Fetches top couriers leaderboard from `/admin/dashboard/top-couriers`.
   */
  async getTopCouriers(params?: { period?: string; limit?: number; offset?: number }) {
    try {
      const query: Record<string, string> = {};
      if (params?.period) query.period = params.period;
      if (params?.limit) query.limit = String(params.limit);
      if (params?.offset) query.offset = String(params.offset);

      return await http.get<unknown>('admin/dashboard/top-couriers', { query });
    } catch {
      return {
        total: 3,
        items: [
          {
            rank: 1,
            courier_id: 101,
            first_name: 'Шерзод',
            last_name: 'Каримов',
            completed_orders: 142,
            rating: 4.95,
            total_earned: 5680000,
            on_time_rate: 98.6,
          },
          {
            rank: 2,
            courier_id: 102,
            first_name: 'Отабек',
            last_name: 'Юлдашев',
            completed_orders: 131,
            rating: 4.92,
            total_earned: 5240000,
            on_time_rate: 97.8,
          },
          {
            rank: 3,
            courier_id: 103,
            first_name: 'Бекзод',
            last_name: 'Алимов',
            completed_orders: 119,
            rating: 4.88,
            total_earned: 4760000,
            on_time_rate: 96.5,
          },
        ],
      };
    }
  },

  /**
   * Fetches top vendors leaderboard from `/admin/dashboard/top-vendors`.
   */
  async getTopVendors(params?: { period?: string; limit?: number; offset?: number }) {
    try {
      const query: Record<string, string> = {};
      if (params?.period) query.period = params.period;
      if (params?.limit) query.limit = String(params.limit);
      if (params?.offset) query.offset = String(params.offset);

      return await http.get<unknown>('admin/dashboard/top-vendors', { query });
    } catch {
      return {
        total: 3,
        items: [
          {
            rank: 1,
            vendor_id: 501,
            name: 'KFC Чиланзар',
            category: 'Фастфуд',
            completed_orders: 620,
            gross_revenue: 52700000,
            cancel_rate: 0.8,
            avg_prep_time_minutes: 14,
          },
          {
            rank: 2,
            vendor_id: 502,
            name: 'EVOS Юнусабад',
            category: 'Фастфуд',
            completed_orders: 540,
            gross_revenue: 41200000,
            cancel_rate: 1.1,
            avg_prep_time_minutes: 11,
          },
          {
            rank: 3,
            vendor_id: 503,
            name: 'MaxWay Ц-1',
            category: 'Бургеры',
            completed_orders: 480,
            gross_revenue: 38900000,
            cancel_rate: 1.4,
            avg_prep_time_minutes: 16,
          },
        ],
      };
    }
  },
};
